const PAGINATION_QUERY_KEYS = new Set(['fields', 'page', 'count', 'sort', 'direction']);

type Translate = (
    key: string,
    interpolationValues?: Record<string, string | number>,
) => string;

type DeletePermissionItem = {
    primaryKey__?: string;
    category__?: string;
};

type DeleteItemsSelection = {
    primaryKeys?: string[];
    itemsQuery?: Record<string, unknown>;
    /** Listed in the dialog instead of the query table. The delete still uses selectionId. */
    confirmPrimaryKeys?: string[];
};

type DeleteConfirmBody =
    | { kind: 'keys'; primaryKeys: string[] }
    | { kind: 'query'; entries: Array<{ key: string; value: string }> }
    | { kind: 'all' };

const cleanKeys = (keys: string[] | undefined): string[] => {
    if (!keys) {
        return [];
    }
    return keys.filter((key) => key.length > 0);
};

const restrictionQuery = (
    query: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
    if (!query) {
        return undefined;
    }

    const next: Record<string, unknown> = {};
    Object.entries(query).forEach(([key, value]) => {
        if (PAGINATION_QUERY_KEYS.has(key) || value === undefined) {
            return;
        }
        next[key] = value;
    });

    return Object.keys(next).length > 0 ? next : undefined;
};

const readSelectionId = (itemsQuery: Record<string, unknown> | undefined): string | undefined => {
    const selectionId = itemsQuery?.selectionId;
    if (typeof selectionId === 'string' && selectionId.length > 0) {
        return selectionId;
    }
    if (typeof selectionId === 'number') {
        return String(selectionId);
    }
    return undefined;
};

const formatQueryValue = (value: unknown): string => {
    if (value == null) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const resolveDeleteConfirmBody = (selection: DeleteItemsSelection): DeleteConfirmBody => {
    const listedKeys = selection.confirmPrimaryKeys?.length
        ? selection.confirmPrimaryKeys
        : selection.primaryKeys;
    const primaryKeys = cleanKeys(listedKeys);
    if (primaryKeys.length > 0) {
        return { kind: 'keys', primaryKeys };
    }

    const itemsQuery = restrictionQuery(selection.itemsQuery);
    if (itemsQuery) {
        return {
            kind: 'query',
            entries: Object.entries(itemsQuery).map(([key, value]) => ({
                key,
                value: formatQueryValue(value),
            })),
        };
    }

    return { kind: 'all' };
};

const selectionPayload = (selection: DeleteItemsSelection): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    const primaryKeys = cleanKeys(selection.primaryKeys);
    const itemsQuery = restrictionQuery(selection.itemsQuery);
    if (primaryKeys.length > 0) {
        payload.primaryKeys = primaryKeys;
    }
    if (itemsQuery) {
        payload.itemsQuery = itemsQuery;
    }
    return payload;
};

const isWatchlistDelete = (
    routeSelectionId: unknown,
    watchlistPrimaryKeys: string[],
): boolean => routeSelectionId != null
    && routeSelectionId !== ''
    && watchlistPrimaryKeys.length > 0;

/**
 * One decision for toolbar Delete and the Angular `browseDeleteItems` listener:
 * watchlist select-all lists the watchlist keys, a query select-all keeps the
 * query, and a normal selection deletes those primary keys.
 */
const deleteRequestForSelection = ({
    allItemsSelected,
    selectedKeys,
    routeSelectionId,
    watchlistPrimaryKeys,
    itemsQuery,
}: {
    allItemsSelected: boolean;
    selectedKeys: string[];
    routeSelectionId: unknown;
    watchlistPrimaryKeys: string[];
    itemsQuery?: Record<string, unknown>;
}): DeleteItemsSelection | null => {
    if (allItemsSelected && isWatchlistDelete(routeSelectionId, watchlistPrimaryKeys)) {
        return {
            itemsQuery: { selectionId: routeSelectionId as string | number },
            confirmPrimaryKeys: watchlistPrimaryKeys,
        };
    }
    if (allItemsSelected) {
        return { itemsQuery };
    }
    if (selectedKeys.length > 0) {
        return { primaryKeys: selectedKeys };
    }
    return null;
};

const deleteRequestFromBroadcast = (
    selection: {
        primaryKeys?: string[] | string;
        itemsQuery?: Record<string, unknown>;
    } | null | undefined,
    routeSelectionId: unknown,
    watchlistPrimaryKeys: string[],
): DeleteItemsSelection => {
    const broadcastKeys = selection?.primaryKeys;
    const primaryKeys = Array.isArray(broadcastKeys) ? cleanKeys(broadcastKeys) : [];
    if (isWatchlistDelete(routeSelectionId, watchlistPrimaryKeys) && primaryKeys.length === 0) {
        return {
            itemsQuery: selection?.itemsQuery ?? { selectionId: routeSelectionId as string | number },
            confirmPrimaryKeys: watchlistPrimaryKeys,
        };
    }
    if (primaryKeys.length > 0) {
        return { primaryKeys };
    }
    return { itemsQuery: selection?.itemsQuery };
};

const deletePermissionWarning = (t: Translate, item: DeletePermissionItem): string => {
    if (item.primaryKey__) {
        return t('ITEM_ACTION.FORBIDDEN_FOR_PRIMARY_KEY', {
            action: 'delete',
            primaryKey: item.primaryKey__,
        });
    }
    if (item.category__) {
        return t('ITEM_ACTION.FORBIDDEN_FOR_CATEGORY', {
            action: 'delete',
            category: item.category__,
        });
    }
    return t('ITEM_ACTION.FORBIDDEN', { action: 'delete' });
};

export type { DeleteItemsSelection, DeletePermissionItem };
export {
    cleanKeys,
    deletePermissionWarning,
    deleteRequestForSelection,
    deleteRequestFromBroadcast,
    readSelectionId,
    resolveDeleteConfirmBody,
    restrictionQuery,
    selectionPayload,
};
